<script setup lang="ts">
import { LoaderIcon, UploadIcon, XCircleIcon } from "lucide-vue-next"
import { computed, ref, useTemplateRef, watch } from "vue"

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group"
import type { Blob as BlobAttachment } from "@/types/serializers"

defineOptions({
  inheritAttrs: false,
})

const fileValue = defineModel<BlobAttachment | null>("file")
const uploading = defineModel<boolean>("loading", { default: false })

const props = withDefaults(
  defineProps<{
    invalid?: boolean
    disabled?: boolean
    name?: string
    accept?: string
    placeholder?: string
    action?: string
  }>(),
  {
    invalid: false,
    disabled: false,
    action: "/attachments",
    placeholder: "Choose a file...",
  },
)
const emit = defineEmits(["input", "change"])

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

const inputRef = useTemplateRef<HTMLInputElement>("input")

const uploadedFile = ref<File | null>(null)
const error = ref<string | null>(null)

const fileName = computed(
  () => uploadedFile.value?.name || fileValue.value?.filename || null,
)

function csrfToken(): string | null {
  const metaTag = document.querySelector<HTMLMetaElement>(
    "meta[name='csrf-token']",
  )
  return metaTag?.content ?? null
}

async function uploadAttachment(file: File): Promise<BlobAttachment> {
  const token = csrfToken()
  const headers: HeadersInit = { Accept: "application/json" }

  if (token) headers["X-CSRF-Token"] = token

  const formData = new FormData()
  formData.append("file", file)

  const response = await fetch(props.action, {
    method: "POST",
    headers,
    credentials: "same-origin",
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`Attachment upload failed with status ${response.status}`)
  }

  return (await response.json()) as BlobAttachment
}

async function uploadFile(file: File): Promise<BlobAttachment | null> {
  if (uploading.value) return null
  try {
    uploading.value = true
    return await uploadAttachment(file)
  } finally {
    uploading.value = false
  }
}

async function deleteFile(id: BlobAttachment["signed_id"] | undefined) {
  if (!id) return
  // await $fetch(props.action + `/${id}`, { method: 'DELETE' })
}

async function handleFileChange(e: Event) {
  const inputTarget = e.target as HTMLInputElement
  const file = inputTarget.files?.[0]
  if (!file) return

  error.value = null

  if (file.size > MAX_FILE_SIZE) {
    error.value = "components.fileInput.size_exceeded"
    inputTarget.value = ""
    return
  }
  try {
    uploadedFile.value = file
    const result = await uploadFile(file)
    if (!result) return
    fileValue.value = result
    emit("change", result)
  } catch (err) {
    uploadedFile.value = null
    console.error(err)
  } finally {
    inputTarget.value = ""
  }
}

async function clear() {
  await deleteFile(fileValue.value?.signed_id)
  fileValue.value = null
  uploadedFile.value = null
  if (inputRef.value) inputRef.value.value = ""
}

watch(
  () => fileValue.value?.signed_id,
  (newIdValue) => {
    if (!newIdValue && uploadedFile.value) uploadedFile.value = null
  },
)
</script>

<template>
  <label :for="name">
    <InputGroup :data-disabled="uploading">
      <InputGroupInput
        :disabled="uploading || disabled"
        :model-value="fileName"
        :placeholder="placeholder"
        v-bind="$attrs"
        :name="`${name}-upload`"
        class="pointer-events-none"
        ref="input"
      />
      <input
        type="file"
        :id="name"
        :accept="accept"
        class="size-0 opacity-0"
        @change="handleFileChange"
      />
      <input type="hidden" :name="name" :value="fileValue?.signed_id ?? ''" />
      <InputGroupAddon>
        <UploadIcon />
      </InputGroupAddon>
      <InputGroupAddon v-if="uploading || fileName" align="inline-end">
        <LoaderIcon v-if="uploading" class="animate-spin" />
        <InputGroupButton
          aria-label="Clear"
          title="Clear file"
          size="icon-xs"
          :disabled="uploading"
          @click="clear"
        >
          <XCircleIcon />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  </label>
</template>
